import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignaturePadLib from 'signature_pad';
import { Eraser, RotateCcw, Undo2 } from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';

/**
 * Touch-, pen- and mouse-friendly signature canvas.
 *
 * Props:
 *   value      data URL (PNG) or null; when provided on mount it is drawn back
 *   onChange   (dataUrl|null) => void, called on every stroke end and on clear
 *   height     css px (default 180)
 *   disabled
 *   label      visually hidden accessible name
 *   error      message rendered below
 */
const SignaturePad = ({ value = null, onChange, height = 180, disabled = false, label = 'Signature', error, className = '' }) => {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [isEmpty, setIsEmpty] = useState(!value);
  const isMobile = useIsMobile();

  onChangeRef.current = onChange;

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const pad = padRef.current;
    if (!canvas || !pad) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const data = pad.toData();
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    pad.clear();
    if (data.length) {
      pad.fromData(data);
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const pad = new SignaturePadLib(canvas, {
      backgroundColor: 'rgba(255,255,255,0)',
      penColor: '#111827',
      minWidth: 0.8,
      maxWidth: 2.4,
      throttle: 8
    });
    padRef.current = pad;

    const handleEnd = () => {
      const empty = pad.isEmpty();
      setIsEmpty(empty);
      if (onChangeRef.current) {
        onChangeRef.current(empty ? null : pad.toDataURL('image/png'));
      }
    };
    pad.addEventListener('endStroke', handleEnd);

    resizeCanvas();
    if (value) {
      pad.fromDataURL(value, { ratio: Math.max(window.devicePixelRatio || 1, 1) }).then(() => setIsEmpty(false)).catch(() => null);
    }

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => resizeCanvas()) : null;
    if (observer) observer.observe(canvas);
    window.addEventListener('orientationchange', resizeCanvas);

    return () => {
      pad.removeEventListener('endStroke', handleEnd);
      pad.off();
      if (observer) observer.disconnect();
      window.removeEventListener('orientationchange', resizeCanvas);
      padRef.current = null;
    };
    // Mount once; `value` is only used as the initial drawing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizeCanvas]);

  useEffect(() => {
    const pad = padRef.current;
    if (!pad) return;
    if (disabled) pad.off(); else pad.on();
  }, [disabled]);

  // External clear (value set back to null by the parent).
  useEffect(() => {
    if (value === null && padRef.current && !padRef.current.isEmpty()) {
      padRef.current.clear();
      setIsEmpty(true);
    }
  }, [value]);

  const handleClear = () => {
    padRef.current?.clear();
    setIsEmpty(true);
    onChangeRef.current?.(null);
  };

  const handleUndo = () => {
    const pad = padRef.current;
    if (!pad) return;
    const data = pad.toData();
    if (!data.length) return;
    data.pop();
    pad.fromData(data);
    const empty = pad.isEmpty();
    setIsEmpty(empty);
    onChangeRef.current?.(empty ? null : pad.toDataURL('image/png'));
  };

  return (
    <div className={className}>
      <div
        className={[
          'relative overflow-hidden rounded-lg border-2 bg-white',
          error ? 'border-red-400' : 'border-dashed border-gray-300',
          disabled ? 'opacity-60' : ''
        ].join(' ')}
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`${label}. Draw with your finger, pen or mouse. Use the typed name field if you cannot draw.`}
          className="h-full w-full touch-none cursor-crosshair"
        />
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-gray-500">
            <span className="text-sm">{isMobile ? 'Sign here with your finger' : 'Sign here with your mouse or trackpad'}</span>
            {isMobile && <span className="mt-1 text-xs">Tip: rotate your phone for more room</span>}
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-6 bottom-8 border-b border-gray-300" aria-hidden="true" />
      </div>

      <div className="mt-2 flex items-center justify-between">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleUndo}
            disabled={disabled || isEmpty}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Undo2 className="h-4 w-4" aria-hidden="true" /> Undo
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled || isEmpty}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Eraser className="h-4 w-4" aria-hidden="true" /> Clear
          </button>
        </div>
        {!isEmpty && (
          <span className="inline-flex items-center gap-1 text-xs text-green-700">
            <RotateCcw className="h-3 w-3" aria-hidden="true" /> Signature captured
          </span>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
};

export default SignaturePad;
